import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LCS_API_CONFIG, LcsApiConfig } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { ApplicationLogin } from '../authentication/models/application-login.interface';
import { sessionIsValid } from '../session/session-validator';
import { UserSessionStorageService } from '../session/user-session-storage.service';

import {
   authenticationVerificationFailure,
   authenticationVerificationSuccess,
   newAppVersionLoaded,
   unverifiedSessionFound,
} from './app-start.actions';

@Injectable()
export class AppStartEffects {
   authenticationVerification = createEffect(() => {
      return this.actions$.pipe(
         ofType(unverifiedSessionFound),
         mergeMap(() => {
            const sessionStatusUrl = `${this.apiConfig.baseUrl}/session/status`;

            const requests = this.http.get<ApplicationLogin>(sessionStatusUrl);

            return requests.pipe(
               map((loginResult: ApplicationLogin) => {
                  if (sessionIsValid(loginResult)) {
                     return authenticationVerificationSuccess({ loginResult });
                  } else {
                     return authenticationVerificationFailure({ error: 'Invalid Session' });
                  }
               }),
               catchError(({ error }) => {
                  return of(authenticationVerificationFailure({ error }));
               })
            );
         })
      );
   });

   updateApplicationVersion$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(authenticationVerificationSuccess),
         map((action) => {
            const oldVersion = this.userSessionStorage.getApplicationVersion();
            const newVersion = action.loginResult.ApplicationVersion;
            return [oldVersion, newVersion] as [string | null, string];
         }),
         filter(([oldVersion, newVersion]) => {
            return !oldVersion || oldVersion !== newVersion;
         }),
         map(([oldVersion, newVersion]) => {
            this.userSessionStorage.setApplicationVersion(newVersion);
            return newAppVersionLoaded({ newVersion, oldVersion });
         })
      );
   });

   constructor(
      private actions$: Actions,
      private http: HttpClient,
      @Inject(LCS_API_CONFIG) private apiConfig: LcsApiConfig,
      private userSessionStorage: UserSessionStorageService
   ) {}
}
